import React, { useEffect } from 'react';
import { useDrag, useDrop } from 'react-dnd';
import { getEmptyImage } from 'react-dnd-html5-backend';
import styled from 'styled-components';
import { toRgba } from '../../../../../ui/theme/colors';
import { ItemTypes } from '../../../../editCourse/utils/dragTypes';

const Wrapper = styled.div`
  opacity: ${({ $isDragging }) => ($isDragging ? 0.5 : 1)};
`;

const DropZone = styled.div`
  height: ${({ $isOver }) => ($isOver ? '60px' : '8px')};
  background: ${({ $isOver, $themeColor }) => ($isOver ? toRgba($themeColor, 0.1) : 'transparent')};
  border: ${({ $isOver, $themeColor }) => ($isOver ? `2px dashed ${toRgba($themeColor, 0.4)}` : 'none')};
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0;
`;

/**
 * DraggableSection - Wrapper component for draggable sections in the structure editor
 */
const DraggableSection = ({ section, index, onDrop, children, themeColor }) => {
  const [{ isDragging }, drag, preview] = useDrag(
    () => ({
      type: ItemTypes.SECTION,
      item: { sectionId: section.id, sectionName: section.name, index },
      collect: (monitor) => ({
        isDragging: monitor.isDragging(),
      }),
    }),
    [section.id, section.name, index],
  );

  const [{ isOver, canDrop }, drop] = useDrop(
    () => ({
      accept: ItemTypes.SECTION,
      drop: (item) => {
        if (item.sectionId !== section.id) {
          onDrop(item.sectionId, index);
        }
      },
      collect: (monitor) => ({
        isOver: monitor.isOver({ shallow: true }),
        canDrop: monitor.canDrop(),
      }),
    }),
    [section.id, index, onDrop],
  );

  const isRenderProp = typeof children === 'function';

  useEffect(() => {
    if (isRenderProp) {
      preview(getEmptyImage(), { captureDraggingState: true });
    }
  }, [isRenderProp, preview]);

  if (isRenderProp) {
    return (
      <>
        <DropZone ref={drop} $isOver={isOver && canDrop} $themeColor={themeColor} />
        <Wrapper $isDragging={isDragging}>{children({ dragRef: drag, isDragging })}</Wrapper>
      </>
    );
  }

  return (
    <>
      <DropZone ref={drop} $isOver={isOver && canDrop} $themeColor={themeColor} />
      <Wrapper ref={drag} $isDragging={isDragging}>
        {children}
      </Wrapper>
    </>
  );
};

export default DraggableSection;
